import { useAuth0 } from "@auth0/auth0-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { startTransition, useEffect, useRef, useState } from "react";
import config from "./config";
import { getForthLeafIdentitySubject } from "./forthLeafIdentity";
import {
  createForthLeafPermissionsQuery,
  createForthLeafPermissionsQueryKey,
  getForthLeafPermissionResponseStatus,
  resolveForthLeafIdentity,
  type ForthLeafIdentity,
  type PermissionsQueryKey,
} from "./forthLeafPermissions";

type SessionStatus = "authorized" | "denied" | "error" | "loading";

interface ForthLeafSession {
  activePermissions: string[];
  identity: ForthLeafIdentity | undefined;
  retry: () => void;
  status: SessionStatus;
}

const rootUrl = () => window.location.origin;

export const useForthLeafSession = (): ForthLeafSession => {
  const { getAccessTokenSilently, getIdTokenClaims, isAuthenticated, isLoading, logout, user } =
    useAuth0();
  const [identity, setIdentity] = useState<ForthLeafIdentity>();
  const [identityError, setIdentityError] = useState<unknown>();
  const [identityAttempt, setIdentityAttempt] = useState(0);
  const identitySubjectHint = getForthLeafIdentitySubject(user);
  const identityChanged = Boolean(
    identitySubjectHint && identity && identity.subject !== identitySubjectHint,
  );
  const activeIdentity = identityChanged ? undefined : identity;

  useEffect(() => {
    if (isLoading || !isAuthenticated || !config.enforceForthLeafAccess) {
      startTransition(() => {
        setIdentity(undefined);
        setIdentityError(undefined);
      });
      return;
    }

    let cancelled = false;
    startTransition(() => {
      setIdentity(undefined);
      setIdentityError(undefined);
    });

    const resolveIdentity = async () => {
      try {
        const resolvedIdentity = await resolveForthLeafIdentity(getIdTokenClaims);

        if (!cancelled) {
          setIdentity(resolvedIdentity);
        }
      } catch (error) {
        if (!cancelled) {
          setIdentityError(error);
        }
      }
    };

    void resolveIdentity();

    return () => {
      cancelled = true;
    };
  }, [getIdTokenClaims, identityAttempt, identitySubjectHint, isAuthenticated, isLoading]);

  const permissionIdentity =
    config.enforceForthLeafAccess && isAuthenticated && !isLoading ? activeIdentity : undefined;
  const permissionSubject = permissionIdentity?.subject;
  const permissionEmail = permissionIdentity?.email;
  const permissionQueryOptions = createForthLeafPermissionsQuery({
    getAccessTokenSilently: () => getAccessTokenSilently(),
    identity: permissionIdentity,
  });
  const queryClient = useQueryClient();
  const permissionQuery = useQuery(permissionQueryOptions);
  const responseStatus = permissionIdentity
    ? getForthLeafPermissionResponseStatus(permissionQuery.error)
    : undefined;
  const previousPermissionQueryKey = useRef<PermissionsQueryKey | undefined>(undefined);

  useEffect(() => {
    const currentQueryKey = permissionIdentity
      ? createForthLeafPermissionsQueryKey(permissionIdentity)
      : undefined;
    const previousQueryKey = previousPermissionQueryKey.current;

    if (
      previousQueryKey &&
      (!currentQueryKey ||
        previousQueryKey[1] !== currentQueryKey[1] ||
        previousQueryKey[2] !== currentQueryKey[2])
    ) {
      void queryClient.cancelQueries({ queryKey: previousQueryKey });
      queryClient.removeQueries({ queryKey: previousQueryKey });
    }

    previousPermissionQueryKey.current = currentQueryKey;
  }, [permissionEmail, permissionIdentity, permissionSubject, queryClient]);

  useEffect(() => {
    if (!permissionSubject || !permissionEmail) {
      return;
    }

    const queryKey = createForthLeafPermissionsQueryKey(permissionIdentity);

    return () => {
      void queryClient.cancelQueries({ queryKey });
    };
  }, [permissionEmail, permissionIdentity, permissionSubject, queryClient]);

  useEffect(() => {
    if (responseStatus !== 401 || !isAuthenticated || !permissionSubject || !permissionEmail) {
      return;
    }

    void logout({ logoutParams: { returnTo: rootUrl() } });
  }, [isAuthenticated, logout, permissionEmail, permissionSubject, responseStatus]);

  const retry = () => {
    if (!permissionIdentity) {
      setIdentityAttempt((currentAttempt) => currentAttempt + 1);
      return;
    }

    void permissionQuery.refetch();
  };
  const createSessionResult = (
    status: SessionStatus,
    activePermissions: string[] = [],
    admittedIdentity: ForthLeafIdentity | undefined = undefined,
  ) => ({
    activePermissions,
    identity: admittedIdentity,
    retry,
    status,
  });

  if (isLoading || !isAuthenticated) {
    return createSessionResult("loading");
  }

  if (!config.enforceForthLeafAccess) {
    return createSessionResult("authorized");
  }

  if (identityChanged) {
    return createSessionResult("loading");
  }

  if (identityError) {
    return createSessionResult("error");
  }

  if (
    !activeIdentity ||
    permissionQuery.isPending ||
    (!permissionQuery.data && permissionQuery.isFetching)
  ) {
    return createSessionResult("loading");
  }

  if (responseStatus === 403) {
    return createSessionResult("denied");
  }

  if (permissionQuery.isError || !permissionQuery.data) {
    return createSessionResult("error");
  }

  const activePermissions = permissionQuery.data;

  return createSessionResult(
    activePermissions.includes(config.forthLeafPermission) ? "authorized" : "denied",
    activePermissions,
    activePermissions.includes(config.forthLeafPermission) ? activeIdentity : undefined,
  );
};
